import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Paper,
  Tooltip,
  Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';

// Column code → friendly label (mirrors instant_update.php TRACKED_FIELDS)
const FIELD_LABELS = {
  MARKA: 'Brand',
  stock: 'Stock',
  PRICE: 'Price',
  EAN: 'EAN',
  WEIGHT: 'Weight',
  FOTO_URL: 'Photo',
  datasheet: 'Datasheet',
  DESCRIPTION_EN: 'Description (EN)',
  DESCRIPTION_FIN: 'Description (FI)',
  DESCRIPTION_FR: 'Description (FR)',
  DESCRIPTION_DE: 'Description (DE)',
  CAT_ID: 'Category',
  PARENT_ID: 'Parent category',
  STATUS: 'Status'
};

const SOURCE_META = {
  sheet_push: { label: 'Manual push',   icon: <TouchAppIcon fontSize="inherit" />, bg: '#e0e7ff', fg: '#3730a3' },
  autosync:   { label: 'Auto-sync',     icon: <CloudSyncIcon fontSize="inherit" />, bg: '#e0f2fe', fg: '#075985' },
  cron:       { label: 'Cron',          icon: <ScheduleIcon fontSize="inherit" />, bg: '#f3e8ff', fg: '#6b21a8' },
  admin:      { label: 'Admin edit',    icon: <PersonIcon fontSize="inherit" />,   bg: '#fce7f3', fg: '#9d174d' }
};

const formatValue = (v, field) => {
  if (v == null || v === '') return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>empty</span>;
  const str = String(v);
  if (field === 'FOTO_URL' || field === 'datasheet') {
    return (
      <a href={str} target="_blank" rel="noreferrer" style={{ color: '#845adf', textDecoration: 'underline', wordBreak: 'break-all' }}>
        {str.length > 60 ? str.substring(0, 60) + '…' : str}
      </a>
    );
  }
  if (str.length > 120) return str.substring(0, 120) + '…';
  return str;
};

// Server sends ISO 8601 UTC (with trailing Z). Browser converts to local tz.
// Fallback: if a legacy "YYYY-MM-DD HH:MM:SS" arrives, assume it's UTC too.
const parseServerDate = (iso) => {
  if (!iso) return null;
  const s = iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z';
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (iso) => {
  const d = parseServerDate(iso);
  if (!d) return { rel: iso, abs: iso };
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const localAbs = d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false
  });
  if (diffMin < 1)  return { rel: 'just now',        abs: localAbs };
  if (diffMin < 60) return { rel: `${diffMin} min ago`, abs: localAbs };
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return { rel: `${diffHr}h ago`, abs: localAbs };
  return { rel: d.toLocaleDateString('en-GB'), abs: localAbs };
};

const formatLocalTime = (iso) => {
  const d = parseServerDate(iso);
  if (!d) return iso;
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false
  });
};

const LogRow = ({ log }) => {
  const [open, setOpen] = useState(false);
  const isInsert = log.action === 'inserted';
  const src = SOURCE_META[log.source] || { label: log.source, icon: null, bg: '#f3f4f6', fg: '#374151' };
  const time = formatDate(log.created_at);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        border: '1px solid #e5e7eb',
        borderLeft: `4px solid ${isInsert ? '#16a34a' : '#845adf'}`,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.15s',
        '&:hover': { borderColor: '#d1d5db', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#fafafa' }
        }}
      >
        <Box sx={{
          width: 36, height: 36, borderRadius: '50%',
          backgroundColor: isInsert ? '#dcfce7' : '#ede9fe',
          color: isInsert ? '#16a34a' : '#7c3aed',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          {isInsert ? <AddCircleIcon /> : <EditIcon />}
        </Box>

        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>
              SKU {log.sku}
            </Typography>
            <Chip
              size="small"
              label={isInsert ? 'INSERT' : 'UPDATE'}
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                backgroundColor: isInsert ? '#dcfce7' : '#ede9fe',
                color: isInsert ? '#166534' : '#5b21b6',
                letterSpacing: '0.5px'
              }}
            />
            <Chip
              size="small"
              icon={src.icon}
              label={src.label}
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 500,
                backgroundColor: src.bg,
                color: src.fg,
                '& .MuiChip-icon': { color: src.fg, fontSize: 12 }
              }}
            />
            {!isInsert && log.changed_count > 0 && (
              <Chip
                size="small"
                label={`${log.changed_count} field${log.changed_count === 1 ? '' : 's'} changed`}
                sx={{ height: 20, fontSize: 10, backgroundColor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
              />
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <Tooltip title={time.abs}>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                {time.rel}
              </Typography>
            </Tooltip>
            {!isInsert && log.changed_fields?.length > 0 && !open && (
              <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: 11 }}>
                · {log.changed_fields.slice(0, 4).map(f => FIELD_LABELS[f] || f).join(', ')}
                {log.changed_fields.length > 4 && ` +${log.changed_fields.length - 4}`}
              </Typography>
            )}
          </Box>
        </Box>

        <IconButton size="small" sx={{ color: '#9ca3af' }}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2, pb: 2, pt: 0, backgroundColor: '#fafbfc', borderTop: '1px solid #f3f4f6' }}>
          {isInsert ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#16a34a', fontWeight: 600, mb: 0.5 }}>
                ✨ New product added
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                All fields initialized from the sheet payload.
              </Typography>
            </Box>
          ) : Object.keys(log.changes).length === 0 ? (
            <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', py: 2, textAlign: 'center' }}>
              No field-level changes recorded.
            </Typography>
          ) : (
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              {Object.entries(log.changes).map(([field, diff]) => (
                <Grid item xs={12} md={6} key={field}>
                  <Box sx={{
                    p: 1.5,
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: 1,
                  }}>
                    <Typography variant="caption" sx={{
                      fontWeight: 700,
                      color: '#845adf',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      display: 'block',
                      mb: 0.75,
                      fontSize: 10
                    }}>
                      {FIELD_LABELS[field] || field}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Box sx={{
                        flex: 1,
                        minWidth: 0,
                        p: 1,
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: 0.5,
                        textDecoration: 'line-through',
                        textDecorationColor: '#dc2626',
                        color: '#991b1b',
                        fontSize: 12,
                        wordBreak: 'break-word',
                        lineHeight: 1.4
                      }}>
                        {formatValue(diff.old, field)}
                      </Box>

                      <ArrowRightAltIcon sx={{ color: '#845adf', flexShrink: 0 }} />

                      <Box sx={{
                        flex: 1,
                        minWidth: 0,
                        p: 1,
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: 0.5,
                        color: '#166534',
                        fontSize: 12,
                        fontWeight: 500,
                        wordBreak: 'break-word',
                        lineHeight: 1.4
                      }}>
                        {formatValue(diff.new, field)}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #e5e7eb', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              <strong>Log ID:</strong> #{log.id}
            </Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              <strong>Time:</strong> {formatLocalTime(log.created_at)}
            </Typography>
            {log.ip_address && (
              <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                <strong>IP:</strong> {log.ip_address}
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

const ProductLogList = ({ logs }) => {
  if (!logs || logs.length === 0) return null;

  // Group by LOCAL date so a Baku user sees "Today" using their own midnight,
  // not the UTC one (which would put 3-4am local into "Yesterday").
  const localDateKey = (iso) => {
    const d = parseServerDate(iso);
    if (!d) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const groups = {};
  logs.forEach(log => {
    const dateKey = localDateKey(log.created_at);
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(log);
  });

  const todayLocal = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const yesterdayLocal = (() => {
    const d = new Date(Date.now() - 86400000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const today = todayLocal;
  const yesterday = yesterdayLocal;
  const labelFor = (key) => {
    if (key === today) return 'Today';
    if (key === yesterday) return 'Yesterday';
    try {
      return new Date(key).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return key; }
  };

  return (
    <Box>
      {Object.entries(groups).map(([date, dayLogs]) => (
        <Box key={date} sx={{ mb: 3 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 1.5,
            position: 'sticky',
            top: 0,
            backgroundColor: '#f9fafb',
            py: 1,
            zIndex: 2
          }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 12 }}>
              {labelFor(date)}
            </Typography>
            <Box sx={{ flex: 1, height: 1, backgroundColor: '#e5e7eb' }} />
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              {dayLogs.length} {dayLogs.length === 1 ? 'entry' : 'entries'}
            </Typography>
          </Box>
          {dayLogs.map(log => <LogRow key={log.id} log={log} />)}
        </Box>
      ))}
    </Box>
  );
};

export default ProductLogList;
